import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { type ColumnDef, type RowSelectionState } from "@tanstack/react-table";
import {
  Info,
  Pause,
  Play,
  RefreshCw,
  Search,
  Settings2,
  SlidersHorizontal,
  Trash2,
  Upload,
  Users,
} from "lucide-react";
import { ApiError } from "@/lib/api";
import { useProxies } from "@/lib/proxies";
import {
  type AccountSummary,
  type ImportBatchResponse,
  deriveAccountStatus,
  describeStatusReason,
  formatAccountNo,
  useAccounts,
  useDeleteAccount,
  usePatchAccount,
  useTestAccount,
} from "@/lib/accounts";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { StatusBadge, Badge } from "@/components/ui/badge";
import { ActionMenu } from "@/components/ui/dropdown-menu";
import { SimpleSelect } from "@/components/ui/select";
import { DataTable } from "@/components/ui/table";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader } from "@/components/ui/page-header";
import { ConfirmDialog } from "@/components/ui/dialog";
import { Tooltip } from "@/components/ui/tooltip";
import { TooltipProvider } from "@/components/ui/tooltip";
import { formatRelative } from "@/lib/format";
import { HelpSection } from "@/components/HelpSection";
import { pushToast } from "@/components/Toaster";
import { BulkActionBar } from "@/components/accounts/BulkActionBar";
import { ConversationsToggle } from "@/components/accounts/ConversationsToggle";
import { AccountDetailDrawer } from "@/components/accounts/AccountDetailDrawer";
import { BulkCheckProgress } from "@/components/accounts/BulkCheckProgress";
import { ImportDialog } from "@/components/accounts/ImportDialog";
import { ImportResultDialog } from "@/components/accounts/ImportResultDialog";
import {
  ActiveFilterChips,
  type FilterChip,
  FleetCounters,
} from "@/components/accounts/FleetCounters";
import {
  type AccountFilters,
  AGE_BANDS,
  ALL,
  CHECKED_BANDS,
  DERIVED_STATUSES,
  EMPTY_FILTERS,
  MORE_FILTER_KEYS,
  NONE,
  ROLES,
  TWOFA_STATES,
  activeFilterKeys,
  countFleet,
  filtersFromSearch,
  filtersToSearch,
  matchesBaseFilters,
  matchesFilters,
  queryAccountNo,
} from "@/components/accounts/accountFilters";

// The Status filter always offers the first five statuses; the rest only when
// some account is in that state (or it's the current filter).
const ALWAYS_SHOWN_STATUSES = 5;

/** Filters live in the URL so the Proxies page and the dashboard can link in. */
function useUrlFilters() {
  const [filters, setFilters] = useState<AccountFilters>(() =>
    filtersFromSearch(window.location.search),
  );
  useEffect(() => {
    // Never rewrite another page's URL on the way out.
    if (window.location.pathname !== "/accounts") return;
    const qs = filtersToSearch(filters);
    window.history.replaceState(null, "", `/accounts${qs ? `?${qs}` : ""}`);
  }, [filters]);
  // The sidebar link to this same page arrives as a popstate: re-read the URL.
  useEffect(() => {
    const onPop = () => setFilters(filtersFromSearch(window.location.search));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const patch = (p: Partial<AccountFilters>) => setFilters((f) => ({ ...f, ...p }));
  return { filters, setFilters, patch };
}

// Max points per factor of the health score (app/core/account_score.py).
const HEALTH_FACTORS: [string, number][] = [
  ["bans", 30],
  ["profile", 20],
  ["activity", 20],
  ["age", 15],
  ["session", 15],
];

/** What the health score is made of — next to the Health filter. */
function HealthHint() {
  const { t } = useTranslation();
  return (
    <div className="w-60 space-y-2 py-0.5">
      <p>{t("accounts.filter.health_hint_lead")}</p>
      <ul className="space-y-0.5">
        {HEALTH_FACTORS.map(([key, max]) => (
          <li key={key} className="flex justify-between gap-4">
            <span>{t(`accounts.health.${key}`)}</span>
            <span className="tabular-nums text-fg-muted">
              {t("accounts.filter.health_upto", { max })}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-fg-muted">{t("accounts.filter.health_hint_tail")}</p>
    </div>
  );
}

/**
 * A fresh account (never successfully tested, still warming up) freezes if you
 * run the Test endpoint on it (ContactsSearch). Gate the "Проверить" action for
 * those — see feedback_test_button_freeze_trap.
 */
function isTestGated(a: AccountSummary): boolean {
  return !a.warmup_completed && a.last_check_at === null;
}

export function AccountsPage() {
  const { t } = useTranslation();
  const accounts = useAccounts();
  const proxies = useProxies();
  const test = useTestAccount();
  const patch = usePatchAccount();
  const del = useDeleteAccount();

  const [showImport, setShowImport] = useState(false);
  const [importResult, setImportResult] = useState<ImportBatchResponse | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [deleteTarget, setDeleteTarget] = useState<AccountSummary | null>(null);
  const { filters, setFilters, patch: patchFilters } = useUrlFilters();
  const [showMore, setShowMore] = useState(() =>
    MORE_FILTER_KEYS.some((k) => filters[k] !== EMPTY_FILTERS[k]),
  );

  const data = useMemo(() => accounts.data ?? [], [accounts.data]);

  // Distinct groups (source_label) for the filter dropdown. The value a link
  // asked for stays listed even when no account has it any more, so the
  // select and its chip show it instead of going blank.
  const groups = useMemo(() => {
    const set = new Set<string>();
    for (const a of data) if (a.source_label) set.add(a.source_label);
    if (filters.group !== ALL && filters.group !== NONE) set.add(filters.group);
    return Array.from(set).sort((x, y) => x.localeCompare(y));
  }, [data, filters.group]);

  // Distinct country codes for the geo filter (same rule).
  const countries = useMemo(() => {
    const set = new Set<string>();
    for (const a of data) if (a.country_code) set.add(a.country_code);
    if (filters.country !== ALL && filters.country !== NONE) set.add(filters.country);
    return Array.from(set).sort((x, y) => x.localeCompare(y));
  }, [data, filters.country]);

  // Every proxy of the company, for the "More filters" picker and the chip
  // label — a proxy that just lost its accounts (or was deleted) still reads
  // as a name, not a UUID.
  const proxyOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of proxies.data ?? []) map.set(p.id, p.label);
    for (const a of data) {
      if (a.proxy_id && !map.has(a.proxy_id)) map.set(a.proxy_id, a.proxy_label ?? a.proxy_id);
    }
    const wanted = filters.proxy;
    if (proxies.data && wanted !== ALL && wanted !== NONE && !map.has(wanted)) {
      map.set(wanted, t("accounts.filter.proxy_deleted"));
    }
    return Array.from(map, ([value, label]) => ({ value, label })).sort((x, y) =>
      x.label.localeCompare(y.label),
    );
  }, [data, proxies.data, filters.proxy, t]);

  // Counters are taken over every filter except status/issue, so each number
  // is exactly what clicking it lists.
  const baseData = useMemo(
    () => data.filter((a) => matchesBaseFilters(a, filters)),
    [data, filters],
  );
  const counts = useMemo(() => countFleet(baseData), [baseData]);
  const filteredData = useMemo(() => {
    const rows = baseData.filter((a) => matchesFilters(a, filters));
    // "64" lists #064 first, ahead of TG ids and phones that merely contain 64.
    const no = queryAccountNo(filters.q);
    if (no === null) return rows;
    return [...rows.filter((a) => a.seq_no === no), ...rows.filter((a) => a.seq_no !== no)];
  }, [baseData, filters]);
  const activeKeys = activeFilterKeys(filters);
  const moreActive = MORE_FILTER_KEYS.filter((k) => activeKeys.includes(k)).length;

  // Bulk actions touch only rows the operator can see: a row hidden by a
  // filter stays ticked in the table state, but must not be deleted blind.
  const visibleIds = useMemo(() => new Set(filteredData.map((a) => a.id)), [filteredData]);
  const selectedIds = Object.keys(rowSelection).filter(
    (id) => rowSelection[id] && visibleIds.has(id),
  );

  const detailAccount = useMemo(
    () => data.find((a) => a.id === detailId) ?? null,
    [data, detailId],
  );

  const openDetail = (a: AccountSummary) => {
    setDetailId(a.id);
    setDrawerOpen(true);
  };

  const runTest = (a: AccountSummary) => {
    test.mutate(a.id, {
      // The endpoint answers 200 even when the check found the session dead —
      // the verdict is in the returned account, not in the status code.
      onSuccess: (checked) =>
        pushToast(
          checked.last_check_error || checked.status === "banned" || checked.status === "auth_required"
            ? { kind: "warning", text: t("accounts.test_failed") }
            : { kind: "success", text: t("accounts.test_done") },
        ),
      onError: (err) =>
        pushToast({
          kind: "error",
          text: err instanceof ApiError ? err.detail : t("accounts.error_unknown"),
        }),
    });
  };

  const togglePause = (a: AccountSummary) => {
    // Pause anything not already paused (active/warming_up); resume → active
    // (backend restores warming_up if warmup never finished).
    const next = a.status === "paused" ? "active" : "paused";
    patch.mutate({ id: a.id, status: next });
  };

  const onDelete = async () => {
    if (!deleteTarget) return;
    try {
      await del.mutateAsync(deleteTarget.id);
      pushToast({ kind: "success", text: t("accounts.deleted") });
    } catch (err) {
      pushToast({
        kind: "error",
        text: err instanceof ApiError ? err.detail : t("accounts.error_unknown"),
      });
    }
  };

  const columns = useMemo<ColumnDef<AccountSummary, unknown>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllRowsSelected()
                ? true
                : table.getIsSomeRowsSelected()
                  ? "indeterminate"
                  : false
            }
            onCheckedChange={(v) => table.toggleAllRowsSelected(!!v)}
            aria-label={t("accounts.select_all")}
            onClick={(e) => e.stopPropagation()}
          />
        ),
        cell: ({ row }) => (
          <span onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={row.getIsSelected()}
              onCheckedChange={(v) => row.toggleSelected(!!v)}
              aria-label={t("accounts.select")}
            />
          </span>
        ),
        enableSorting: false,
        size: 32,
      },
      {
        id: "account",
        header: t("accounts.col.account"),
        accessorFn: (a) => a.tg_username ?? a.display_name,
        cell: ({ row }) => {
          const a = row.original;
          return (
            <div className="flex items-center gap-2.5">
              <Avatar
                name={a.tg_username ? `@${a.tg_username}` : a.display_name}
                src={a.has_avatar ? `/api/accounts/${a.id}/avatar` : null}
                colorKey={a.id}
                size={32}
              />
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="shrink-0 font-mono text-xs text-fg-muted">
                    {formatAccountNo(a.seq_no)}
                  </span>
                  <span className="truncate font-semibold text-fg-primary">
                    {a.tg_username ? `@${a.tg_username}` : a.display_name}
                  </span>
                  {a.is_premium && (
                    <Badge tone="accent" className="shrink-0">
                      {t("accounts.premium")}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-fg-muted">
                  {a.tg_username && <span className="truncate">{a.display_name}</span>}
                  {a.tg_user_id != null && (
                    <span className="font-mono">id:{a.tg_user_id}</span>
                  )}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        id: "status",
        header: t("accounts.col.status"),
        accessorFn: (a) => deriveAccountStatus(a),
        cell: ({ row }) => {
          const a = row.original;
          const ds = deriveAccountStatus(a);
          const badge = (
            <StatusBadge
              domain="account"
              value={ds}
              label={t(`accounts.status.${ds}`)}
              dot
            />
          );
          const withReason = a.status_reason ? (
            // StatusBadge takes no ref, so the trigger needs a DOM element of
            // its own — without it the reason tooltip never opened.
            <Tooltip content={describeStatusReason(a.status_reason, t)}>
              <span className="inline-flex">{badge}</span>
            </Tooltip>
          ) : (
            badge
          );
          // A spam limit is orthogonal to the account status — the row stays
          // "active" while TG refuses every invite and cold DM from it. Without
          // this marker the only way to notice was the campaign failing.
          if (a.spam_state !== "limited") return withReason;
          return (
            // Wrap instead of widening: one spam-limited row used to stretch the
            // whole Статус column by ~60px and push the table into a scrollbar.
            <span className="inline-flex max-w-[7.5rem] flex-wrap items-center gap-1.5">
              {withReason}
              <Tooltip content={t("accounts.spam_limited_tooltip")}>
                <span className="rounded bg-danger/15 px-1.5 py-0.5 text-[10px] font-medium leading-none text-danger">
                  {t("accounts.filter.spam_limited")}
                </span>
              </Tooltip>
            </span>
          );
        },
      },
      {
        id: "role",
        header: t("accounts.col.role"),
        accessorFn: (a) => a.role,
        cell: ({ row }) => {
          const a = row.original;
          const label =
            a.role === "demoted_scraper"
              ? t("accounts.role.demoted_scraper")
              : t(`accounts.role.${a.role}`);
          if (a.is_peer_flood_permanent) {
            return (
              <Tooltip content={t("accounts.peer_flood_perm")}>
                <span className="inline-flex items-center gap-1">
                  {label}
                  <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-danger" />
                </span>
              </Tooltip>
            );
          }
          return <span className="whitespace-nowrap">{label}</span>;
        },
      },
      {
        id: "group",
        header: t("accounts.col.group"),
        // Seller tag — analytics context, not per-row triage. Below 1536px it
        // is what made the actions column overflow, and it lives in the
        // account drawer and the analytics tab anyway.
        meta: { className: "hidden 2xl:table-cell" },
        accessorFn: (a) => a.source_label ?? "",
        cell: ({ row }) => {
          const label = row.original.source_label;
          return label ? (
            <Badge tone="neutral">{label}</Badge>
          ) : (
            <span className="text-fg-muted">—</span>
          );
        },
      },
      {
        id: "proxy",
        header: t("accounts.col.proxy"),
        accessorFn: (a) => a.proxy_label ?? "",
        cell: ({ row }) => {
          const a = row.original;
          if (!a.proxy_label) return <span className="text-fg-muted">—</span>;
          const dead = a.proxy_is_alive === false;
          return (
            <span className="inline-flex max-w-[11rem] items-center gap-1.5 font-mono text-xs">
              <span
                aria-hidden="true"
                className={`h-2 w-2 shrink-0 rounded-full ${dead ? "bg-danger" : "bg-success"}`}
              />
              <span className="truncate">{a.proxy_label}</span>
            </span>
          );
        },
      },
      {
        id: "last_check",
        header: t("accounts.col.last_check"),
        accessorFn: (a) => a.last_check_at ?? "",
        cell: ({ row }) => {
          const a = row.original;
          return (
            <span className="whitespace-nowrap text-fg-muted">
              {a.last_check_at ? formatRelative(a.last_check_at) : "—"}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: () => <span className="sr-only">{t("accounts.col.actions")}</span>,
        enableSorting: false,
        cell: ({ row }) => {
          const a = row.original;
          const gated = isTestGated(a);
          return (
            <div
              className="flex items-center justify-end gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                size="sm"
                variant="outline"
                onClick={() => openDetail(a)}
                aria-label={t("accounts.configure")}
                title={t("accounts.configure")}
                iconLeft={<Settings2 size={15} strokeWidth={2} aria-hidden="true" />}
              >
                {/* Below ~1400px the label is what pushed the row actions
                    off the right edge of the table. */}
                <span className="hidden min-[1400px]:inline">
                  {t("accounts.configure")}
                </span>
              </Button>
              <ActionMenu
                label={t("accounts.col.actions")}
                items={[
                  {
                    label: gated ? t("accounts.test_gated") : t("accounts.test"),
                    icon: <RefreshCw size={15} strokeWidth={2} aria-hidden="true" />,
                    onSelect: () => runTest(a),
                    disabled: gated || test.isPending,
                  },
                  a.status === "active" || a.status === "warming_up"
                    ? {
                        label: t("accounts.pause"),
                        icon: <Pause size={15} strokeWidth={2} aria-hidden="true" />,
                        onSelect: () => togglePause(a),
                        disabled: patch.isPending,
                      }
                    : a.status === "paused"
                      ? {
                          label: t("accounts.resume"),
                          icon: <Play size={15} strokeWidth={2} aria-hidden="true" />,
                          onSelect: () => togglePause(a),
                          disabled: patch.isPending,
                        }
                      : {
                          label: t("accounts.pause"),
                          icon: <Pause size={15} strokeWidth={2} aria-hidden="true" />,
                          onSelect: () => {},
                          disabled: true,
                        },
                  {
                    label: t("accounts.delete"),
                    icon: <Trash2 size={15} strokeWidth={2} aria-hidden="true" />,
                    onSelect: () => setDeleteTarget(a),
                    destructive: true,
                    separatorBefore: true,
                  },
                ]}
              />
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, test.isPending, patch.isPending],
  );

  const chipValue = (key: keyof AccountFilters, v: string): string => {
    switch (key) {
      case "q":
        return `"${v}"`;
      case "status":
        return t(`accounts.status.${v}`);
      case "issue":
        return v === "any" ? t("accounts.counters.all_issues") : t(`accounts.issue.${v}`);
      case "group":
        return v === NONE ? t("accounts.filter.group_none") : v;
      case "spam":
        return t(`accounts.filter.spam_${v}`);
      case "country":
        return v === NONE ? t("accounts.filter.country_none") : v;
      case "health":
        return t(`accounts.filter.health_${v}`);
      case "role":
        return t(`accounts.role.${v}`);
      case "proxy":
        return v === NONE
          ? t("accounts.filter.proxy_none")
          : (proxyOptions.find((o) => o.value === v)?.label ?? v);
      case "checked":
        return t(`accounts.filter.checked_${v}`);
      case "twofa":
        return t(`accounts.filter.twofa_${v}`);
      case "age":
        return t(`accounts.filter.age_${v}`);
    }
  };
  const chips: FilterChip[] = activeKeys.map((key) => ({
    key,
    label: t(`accounts.filter.${key === "q" ? "search" : key}`),
    value: chipValue(key, filters[key]),
  }));

  const statusOptions = DERIVED_STATUSES.filter(
    (s, i) => i < ALWAYS_SHOWN_STATUSES || (counts.byStatus[s] ?? 0) > 0 || filters.status === s,
  ).map((s) => ({
    value: s,
    label: `${t(`accounts.status.${s}`)} (${counts.byStatus[s] ?? 0})`,
  }));

  return (
    <TooltipProvider delayDuration={300}>
      <section className="mx-auto max-w-7xl space-y-6 px-6 py-8">
        <PageHeader
          title={t("accounts.title")}
          subtitle={
            data.length > 0 ? (
              <FleetCounters counts={counts} filters={filters} onChange={patchFilters} />
            ) : (
              t("accounts.subtitle")
            )
          }
          actions={
            <Button
              onClick={() => setShowImport(true)}
              iconLeft={<Upload size={16} strokeWidth={2} aria-hidden="true" />}
            >
              {t("accounts.import.title")}
            </Button>
          }
        />

        {accounts.isLoading && <p className="text-fg-muted">{t("accounts.loading")}</p>}
        {accounts.isError && <p className="text-danger">{t("accounts.load_error")}</p>}

        {/* Fleet-to-fleet conversations toggle — only meaningful with ≥2 accounts. */}
        {accounts.data && data.length > 1 && <ConversationsToggle />}

        <BulkCheckProgress />

        {selectedIds.length > 0 && (
          <BulkActionBar
            selectedIds={selectedIds}
            onCleared={() => setRowSelection({})}
          />
        )}

        {accounts.data && data.length > 0 && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-x-3 gap-y-2 sm:flex sm:flex-wrap sm:items-center sm:gap-x-4">
              <div className="relative col-span-2 sm:w-60">
                <Search
                  size={15}
                  strokeWidth={2}
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted"
                />
                <Input
                  type="search"
                  value={filters.q}
                  onChange={(e) => patchFilters({ q: e.target.value })}
                  placeholder={t("accounts.filter.search_placeholder")}
                  aria-label={t("accounts.filter.search_placeholder")}
                  className="h-8 pl-9"
                />
              </div>
              <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                <span className="text-xs text-fg-muted sm:text-sm">{t("accounts.filter.status")}</span>
                <SimpleSelect
                  value={filters.status}
                  // Picking a status replaces an issue filter, like the counters do.
                  onValueChange={(status) => patchFilters({ status, issue: ALL })}
                  options={[
                    { value: ALL, label: t("accounts.filter.status_all") },
                    ...statusOptions,
                  ]}
                  className="h-8 w-full sm:w-48"
                />
              </div>
              {groups.length > 0 && (
                <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                  <span className="text-xs text-fg-muted sm:text-sm">{t("accounts.filter.group")}</span>
                  <SimpleSelect
                    value={filters.group}
                    onValueChange={(group) => patchFilters({ group })}
                    options={[
                      { value: ALL, label: t("accounts.filter.group_all") },
                      ...groups.map((g) => ({ value: g, label: g })),
                      { value: NONE, label: t("accounts.filter.group_none") },
                    ]}
                    className="h-8 w-full sm:w-52"
                  />
                </div>
              )}
              <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                <span className="text-xs text-fg-muted sm:text-sm">{t("accounts.filter.spam")}</span>
                <SimpleSelect
                  value={filters.spam}
                  onValueChange={(spam) => patchFilters({ spam })}
                  options={[
                    { value: ALL, label: t("accounts.filter.spam_all") },
                    { value: "clean", label: t("accounts.filter.spam_clean") },
                    { value: "limited", label: t("accounts.filter.spam_limited") },
                    { value: "unknown", label: t("accounts.filter.spam_unknown") },
                  ]}
                  className="h-8 w-full sm:w-40"
                />
              </div>
              {countries.length > 0 && (
                <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                  <span className="text-xs text-fg-muted sm:text-sm">{t("accounts.filter.country")}</span>
                  <SimpleSelect
                    value={filters.country}
                    onValueChange={(country) => patchFilters({ country })}
                    options={[
                      { value: ALL, label: t("accounts.filter.country_all") },
                      ...countries.map((c) => ({ value: c, label: c })),
                      { value: NONE, label: t("accounts.filter.country_none") },
                    ]}
                    className="h-8 w-full sm:w-36"
                  />
                </div>
              )}
              <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                <span className="inline-flex items-center gap-1 text-xs text-fg-muted sm:text-sm">
                  {t("accounts.filter.health")}
                  <Tooltip content={<HealthHint />}>
                    <button
                      type="button"
                      aria-label={t("accounts.filter.health_about")}
                      className="rounded text-fg-muted hover:text-fg-primary"
                    >
                      <Info size={14} strokeWidth={2} aria-hidden="true" />
                    </button>
                  </Tooltip>
                </span>
                <SimpleSelect
                  value={filters.health}
                  onValueChange={(health) => patchFilters({ health })}
                  options={[
                    { value: ALL, label: t("accounts.filter.health_all") },
                    { value: "high", label: t("accounts.filter.health_high") },
                    { value: "mid", label: t("accounts.filter.health_mid") },
                    { value: "low", label: t("accounts.filter.health_low") },
                  ]}
                  className="h-8 w-full sm:w-44"
                />
              </div>
              <Button
                size="sm"
                variant="ghost"
                aria-expanded={showMore}
                className="col-span-2 justify-self-start"
                onClick={() => setShowMore((v) => !v)}
                iconLeft={<SlidersHorizontal size={15} strokeWidth={2} aria-hidden="true" />}
              >
                {t("accounts.filter.more")}
                {moreActive > 0 && (
                  <span className="ml-1.5 rounded-full bg-accent px-1.5 text-xs font-semibold leading-5 text-fg-on-accent">
                    {moreActive}
                  </span>
                )}
              </Button>
            </div>

            {showMore && (
              <div className="grid grid-cols-2 gap-x-3 gap-y-2 sm:flex sm:flex-wrap sm:items-center sm:gap-x-4">
                <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                  <span className="text-xs text-fg-muted sm:text-sm">{t("accounts.filter.role")}</span>
                  <SimpleSelect
                    value={filters.role}
                    onValueChange={(role) => patchFilters({ role })}
                    options={[
                      { value: ALL, label: t("accounts.filter.role_all") },
                      ...ROLES.map((r) => ({ value: r, label: t(`accounts.role.${r}`) })),
                    ]}
                    className="h-8 w-full sm:w-52"
                  />
                </div>
                <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                  <span className="text-xs text-fg-muted sm:text-sm">{t("accounts.filter.proxy")}</span>
                  <SimpleSelect
                    value={filters.proxy}
                    onValueChange={(proxy) => patchFilters({ proxy })}
                    options={[
                      { value: ALL, label: t("accounts.filter.proxy_all") },
                      ...proxyOptions,
                      { value: NONE, label: t("accounts.filter.proxy_none") },
                    ]}
                    className="h-8 w-full sm:w-52"
                  />
                </div>
                <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                  <span className="text-xs text-fg-muted sm:text-sm">{t("accounts.filter.checked")}</span>
                  <SimpleSelect
                    value={filters.checked}
                    onValueChange={(checked) => patchFilters({ checked })}
                    options={[
                      { value: ALL, label: t("accounts.filter.checked_all") },
                      ...CHECKED_BANDS.map((b) => ({
                        value: b,
                        label: t(`accounts.filter.checked_${b}`),
                      })),
                    ]}
                    className="h-8 w-full sm:w-52"
                  />
                </div>
                <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                  <span className="text-xs text-fg-muted sm:text-sm">{t("accounts.filter.twofa")}</span>
                  <SimpleSelect
                    value={filters.twofa}
                    onValueChange={(twofa) => patchFilters({ twofa })}
                    options={[
                      { value: ALL, label: t("accounts.filter.twofa_all") },
                      ...TWOFA_STATES.map((s) => ({
                        value: s,
                        label: t(`accounts.filter.twofa_${s}`),
                      })),
                    ]}
                    className="h-8 w-full sm:w-40"
                  />
                </div>
                <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                  <span className="text-xs text-fg-muted sm:text-sm">{t("accounts.filter.age")}</span>
                  <SimpleSelect
                    value={filters.age}
                    onValueChange={(age) => patchFilters({ age })}
                    options={[
                      { value: ALL, label: t("accounts.filter.age_all") },
                      ...AGE_BANDS.map((b) => ({ value: b, label: t(`accounts.filter.age_${b}`) })),
                    ]}
                    className="h-8 w-full sm:w-52"
                  />
                </div>
              </div>
            )}

            {chips.length > 0 && (
              <ActiveFilterChips
                chips={chips}
                shown={filteredData.length}
                total={data.length}
                onRemove={(key) => patchFilters({ [key]: EMPTY_FILTERS[key] })}
                onClearAll={() => setFilters(EMPTY_FILTERS)}
              />
            )}
          </div>
        )}

        {accounts.data && (
          <DataTable
            columns={columns}
            data={filteredData}
            getRowId={(a) => a.id}
            onRowClick={openDetail}
            enableRowSelection
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            empty={
              data.length > 0 ? (
                <EmptyState
                  icon={Search}
                  title={t("accounts.filter.no_match_title")}
                  description={t("accounts.filter.no_match_hint")}
                  action={
                    <Button variant="outline" onClick={() => setFilters(EMPTY_FILTERS)}>
                      {t("accounts.filter.clear_all")}
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  icon={Users}
                  title={t("accounts.empty_title")}
                  description={t("accounts.empty_hint")}
                  action={
                    <Button
                      onClick={() => setShowImport(true)}
                      iconLeft={<Upload size={16} strokeWidth={2} aria-hidden="true" />}
                    >
                      {t("accounts.import.title")}
                    </Button>
                  }
                />
              )
            }
          />
        )}

        <HelpSection
          i18nKey="accounts.help"
          paragraphs={5}
          list={{
            titleKey: "accounts.help.list_title",
            itemsKey: "accounts.help.list_items",
            itemCount: 4,
          }}
        />

        <AccountDetailDrawer
          account={detailAccount}
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
        />

        <ImportDialog
          open={showImport}
          onOpenChange={setShowImport}
          onComplete={(res) => {
            setShowImport(false);
            setImportResult(res);
          }}
        />
        {importResult && (
          <ImportResultDialog result={importResult} onClose={() => setImportResult(null)} />
        )}

        <ConfirmDialog
          open={deleteTarget !== null}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          tone="danger"
          title={t("accounts.delete_confirm", {
            name: deleteTarget?.display_name ?? "",
          })}
          confirmText={t("accounts.delete")}
          cancelText={t("common.cancel")}
          onConfirm={onDelete}
        />
      </section>
    </TooltipProvider>
  );
}
